// packages/charts/src/react/echartsRegistry.ts
//
// Side-effect module: registers the ECharts feature set the sandbox uses.
// Using `echarts/core` + explicit `use()` gives us a tree-shaken bundle — the
// full `echarts` entry pulls ~1MB of unused geo/graph code which would land
// inside every sandbox iframe (and every mailed report).
//
// STATIC NAMED IMPORTS, ON PURPOSE. An earlier A5 draft resolved the classes
// from `import * as charts` by name off `ECHARTS_USE`; Rollup cannot
// tree-shake a namespace that is indexed dynamically, and the sandbox entry
// went from 290 KB to 466 KB gzip — every chart family and component in
// ECharts, registered or not — while every test stayed green. The bundle
// price measurement (ADR-0243) is what caught it. So the classes are named
// here, statically, and `__tests__/registryCoverage.test.ts` parses THIS
// file and fails when the identifiers in `use([…])` are not exactly
// `ECHARTS_USE` in `echartsUseList.ts`, in the same order. The name list
// stays the single source the tests and the mocks read; this file is what
// the bundler reads.
//
// WHAT HAS TO BE IN THE LIST, AND WHO SAYS SO. Since plan one-drawing-layer
// (A5, 2026-09-08) the sandbox draws a dashboard `BackendChartSpec` through
// the same option builder as the dashboard tile (`@verity/charts`). That
// builder can put five series families and nine option components into an
// option, and it exports both lists (`ECHARTS_SERIES_FAMILIES`,
// `ECHARTS_OPTION_COMPONENTS`, themselves derived from the goldens and the
// source — see the package's `emissionSet.test.ts`). ECharts does not
// complain about an option key whose class is not registered: it SKIPS it
// (`echarts/lib/model/Global.js`, `if (!ComponentModel.hasClass(mainType))`),
// and a series `markLine` draws only when `installMarkLine` ran. Measured
// here before this list grew: the same option rendered through this core
// with and without `MarkAreaComponent` produced two different SVGs and no
// error. A missing family is a blank chart someone reports; a missing
// component is a chart drawn MINUS a part — no highlight zone, no target
// line, no zoom — with nothing broken to point at.
//
// `registryCoverage.test.ts` reads the builder's two lists and fails by name
// when `ECHARTS_USE` ∪ `SANDBOX_REFUSES` does not cover one of them, and
// `specPicture.test.ts` renders every fixture through a core registered from
// the same names and requires the SVG to equal the app's golden byte for
// byte — the guard at the level of the picture, which is the level the
// silence happens at. Order matters too (see `echartsUseList.ts`).
//
// Guarded by a module-level flag so re-imports (Vitest suites import multiple
// chart components in one process) don't double-register. ECharts internally
// dedupes `use()` calls, but the guard keeps this module fast on re-import
// and makes the behaviour explicit.
import * as echarts from "echarts/core";
import {
  BarChart,
  FunnelChart,
  GaugeChart,
  LineChart,
  PieChart,
  SankeyChart,
  ScatterChart,
  TreemapChart,
} from "echarts/charts";
import {
  AriaComponent,
  AxisPointerComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import {
  LegacyGridContainLabel,
} from "echarts/features";
import {
  CanvasRenderer,
  SVGRenderer,
} from "echarts/renderers";

export { ECHARTS_USE, SANDBOX_REFUSES, echartsUseList } from "./echartsUseList";

let _registered = false;

export function registerECharts(): void {
  if (_registered) return;
  _registered = true;
  // Renderers, families, components, features — the identifiers below are
  // `ECHARTS_USE` in order; the test pins that.
  echarts.use([
    CanvasRenderer,
    SVGRenderer,
    LineChart,
    BarChart,
    PieChart,
    ScatterChart,
    TreemapChart,
    FunnelChart,
    GaugeChart,
    SankeyChart,
    GridComponent,
    TooltipComponent,
    AxisPointerComponent,
    TitleComponent,
    MarkLineComponent,
    MarkAreaComponent,
    DataZoomComponent,
    LegendComponent,
    AriaComponent,
    LegacyGridContainLabel,
  ]);
}

// Register on import so callers don't have to remember to call it.
registerECharts();

export { echarts };
